import React, { useState } from "react";
import "./PaypalWithdrawalOtp.css";
import logo from "../assets/logo.png";
import otpImage from "../assets/otp-support.png";
import { useNavigate, useLocation } from "react-router-dom";

/* ================= WHATSAPP FLOAT COMPONENT ================= */
const WhatsAppFloat = ({ 
  phoneNumber = "15485825756", 
  message = "Hello! I need urgent support from safepalPay customer service for PayPal OTP verification.",
  position = "right",
  bottom = "30px",
  right = "30px",
  left = "auto",
  size = "54px",
  iconSize = "28px",
  pulseEffect = true,
  className = "",
  style = {}
}) => {
  const formattedNumber = phoneNumber.replace(/[^\d]/g, '');
  const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`;
  
  const positionStyles = position === "left" 
    ? { left: left || "20px", right: "auto" }
    : { right: right || "20px", left: "auto" };

  const combinedStyles = {
    position: 'fixed',
    bottom: bottom,
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: '#25d366',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    zIndex: 10000,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    ...positionStyles,
    ...style
  };

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={`whatsapp-float-unique ${pulseEffect ? 'pulse-unique' : ''} ${className}`}
      style={combinedStyles}
      aria-label="Chat on WhatsApp"
      title="Chat on WhatsApp for OTP assistance"
    >
      <svg 
        width={iconSize} 
        height={iconSize} 
        viewBox="0 0 32 32"
        fill="white"
      >
        <path d="M16.001 3C9.373 3 4 8.373 4 15.001c0 2.645.867 5.084 2.329 7.058L5 29l7.142-1.875A11.96 11.96 0 0016 27c6.627 0 12-5.373 12-11.999C28 8.373 22.627 3 16.001 3zm0 21.999c-1.924 0-3.715-.57-5.219-1.548l-.372-.23-4.242 1.114 1.133-4.135-.242-.384A9.957 9.957 0 016 15.001C6 9.486 10.486 5 16.001 5 21.515 5 26 9.486 26 15.001c0 5.514-4.485 9.998-9.999 9.998zm5.548-7.557c-.303-.151-1.793-.886-2.071-.987-.279-.101-.483-.151-.687.151-.202.302-.787.987-.965 1.19-.177.202-.354.227-.656.075-.303-.151-1.279-.471-2.435-1.5-.9-.802-1.508-1.792-1.686-2.094-.177-.303-.019-.467.133-.619.136-.135.303-.354.455-.531.152-.177.202-.303.303-.505.101-.202.051-.379-.025-.531-.076-.151-.687-1.656-.941-2.269-.247-.592-.498-.512-.687-.521-.177-.008-.379-.01-.582-.01-.202 0-.531.075-.808.379-.278.303-1.06 1.035-1.06 2.524 0 1.488 1.085 2.926 1.237 3.128.151.202 2.14 3.27 5.183 4.588.724.312 1.288.498 1.728.637.726.231 1.387.198 1.911.12.583-.087 1.793-.733 2.047-1.44.253-.707.253-1.313.177-1.44-.076-.126-.278-.198-.581-.354z" />
      </svg>
    </a>
  );
};

const PaypalOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Crisp Chat Integration
  React.useEffect(() => {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = "b5635951-f13a-4b92-95d7-c1e3666f3abf";

    window.$crisp.push(["do", "chat:show"]);

    if (!document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:hide"]);
      }
    };
  }, []);

  // Pass user email to Crisp when available
  React.useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    const userName = localStorage.getItem("userName");
    
    if (userEmail && window.$crisp) {
      window.$crisp.push(["set", "user:email", userEmail]);
      if (userName) {
        window.$crisp.push(["set", "user:nickname", userName]);
      }
    }
  }, []);

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`pwo-otp-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`pwo-otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const otpArray = pastedData.split("");
      setOtp(otpArray);
      document.getElementById(`pwo-otp-5`)?.focus();
    }
  };

  /* ================= VERIFY OTP ================= */
  const handleSubmit = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) return;

    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        "https://backend-safepal.onrender.com/api/paypal/verify-otp",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ otp: enteredOtp }),
        }
      );

      const data = await res.json();
      setIsLoading(false);

      if (!data.success) {
        setIsSuccess(false);
        setShowResultPopup(true);
        setTimeout(() => setShowResultPopup(false), 2000);
        return;
      }

      setIsSuccess(true);
      setShowResultPopup(true);

      setTimeout(() => {
        setShowResultPopup(false);

        const payload = {
          transferId: data.data.transferId,
          asset: data.data.asset,
          amount: data.data.amount,
          usdAmount: data.data.usdAmount,
          paypalEmail: data.data.paypalEmail,
          transactionId: data.data.transactionId,
          status: data.data.status,
        };

        sessionStorage.setItem(
          "paypalReceipt",
          JSON.stringify(payload)
        );

        navigate("/paypalreceipt", { state: payload });
      }, 2200);
    } catch (err) {
      console.error("OTP verification error:", err);
      setIsLoading(false);
      setIsSuccess(false);
      setShowResultPopup(true);
      setTimeout(() => setShowResultPopup(false), 2000);
    }
  };

  return (
    <>
      <div className="pwo-otp-page-unique">
        <div className="pwo-otp-box-unique">
          <span className="pwo-otp-back-btn-unique" onClick={() => navigate(-1)}>
            ←
          </span>

          <div className="pwo-otp-header-logo-unique">
            <img src={logo} alt="SafepalPay" />
          </div>

          {/* OTP Illustration Added */}
          <div className="pwo-otp-illustration-unique">
            <img src={otpImage} alt="OTP Verification" />
          </div>

          <h2 className="pwo-otp-heading-unique">Withdrawal Verification</h2>

          <p className="pwo-otp-subtitle-unique">
            Enter the 6-digit code sent to your email
          </p>

          <div 
            className="pwo-otp-inputs-container-unique" 
            onPaste={handlePaste}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`pwo-otp-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                disabled={isLoading}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            className="pwo-otp-confirm-btn-unique"
            onClick={handleSubmit}
            disabled={isLoading || otp.join("").length !== 6}
          >
            {isLoading ? "Verifying..." : "Verify & Complete Transfer"}
          </button>

          {/* Resend Code Link */}
          <p className="pwo-otp-resend-text-unique">
            Didn't receive the code?{" "}
            <button 
              className="pwo-otp-resend-btn-unique"
              onClick={() => {
                alert("A new verification code has been sent to your email.");
              }}
              disabled={isLoading}
            >
              Resend Code
            </button>
          </p>
        </div>
      </div>

      {/* ================= RESULT POPUP ================= */}
      {showResultPopup && (
        <div className="pwo-result-overlay-unique">
          <div className="pwo-result-card-unique">
            {isSuccess ? (
              <>
                <svg className="pwo-success-svg-unique" viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" stroke="#4caf50" strokeWidth="2"/>
                  <path d="M14 27 l7 7 l17 -17" fill="none" stroke="#4caf50" strokeWidth="3" strokeLinecap="round"/>
                </svg>
                <p>OTP Verified Successfully</p>
              </>
            ) : (
              <>
                <svg className="pwo-error-svg-unique" viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" stroke="#f44336" strokeWidth="2"/>
                  <path d="M16 16 L36 36 M36 16 L16 36" fill="none" stroke="#f44336" strokeWidth="3" strokeLinecap="round"/>
                </svg>
                <p>Invalid OTP</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Float Button - For OTP Assistance */}
      <WhatsAppFloat 
        phoneNumber="15485825756"
        message="Hello! I need urgent support from safepalPay customer service for PayPal OTP verification."
        position="right"
        bottom="30px"
        right="30px"
        pulseEffect={true}
        size="54px"
        iconSize="28px"
      />
    </>
  );
};

export default PaypalOtp;